// NPM Imports
import React, { Component } from "react";
import { convertToRaw } from "draft-js";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import uuid from "react-native-uuid";
import { Toastr } from "../../revvComponents";

// Components Imports
import NotesHeader from "./NotesHeader";
import NotesThread from "./NotesThread";

// Actions Imports
import EditorActions from "../../actions/EditorActions";
import {
  acceptRejectSectionChanges,
  updateDocumentClickedComponentID,
  updateComponentEditorState,
  updateComponentMeta,
  updateDynamicFieldsInDoc,
  updateDynamicFieldsInMasterDoc,
  forceRerenderWhenClauseOptionsUpdate,
} from "../../actions/DocumentActions";
import { QuotationActions } from "../../actions";
import NotesActions from "../../actions/NotesActions";
import SharedDocumentActions from "../../actions/SharedDocumentActions";

// Internal Imports
import {
  checkEmptySelection,
  checkSelectedTextHasEntity,
  createHighlightNotes,
  getSelectedText,
} from "../../utils/draftUtils";
import { emptyNote, isNewNoteOpen } from "./utils";
import { readDataFromLocalStorage } from "../../utils/globalUtils";
import { AmplitudeUtils, DocumentUtils } from "../../utils";
import { EVENTS } from "../../constants";
import { NOTES_STRINGS } from "../../stringConstants/index";
import {
  COMPONENT_FEATURE,
  DOCUMENT_COMPONENT_TYPES,
  DRAFT_ENTITY,
} from "../../constants/document";

// Destructuring
const { updateToolPaneData } = QuotationActions;
const {
  getNotesByDocID,
  addNote,
  updateNotes,
  getAllDocNotes,
  postNewNote,
  editNote,
  deleteNote,
  uploadAttachment,
  fetchAllUsersForMentions,
} = NotesActions;
const { getUpdatedParentComponent } = DocumentUtils;
const { createSharedDocumentNote } = SharedDocumentActions;
const { forceUpdateLocalEditorState, initActiveEditor } = EditorActions;

class Notes extends Component {
  state = {
    activeNoteID: null,
  };

  // Rewrite
  // move it fetch user call from here to somewhere else
  componentDidMount() {
    const { isSharedDocument } = this.props;
    if (!isSharedDocument) {
      if (this.props.mentionUsers.length === 0) {
        this.props.fetchAllUsersForMentions();
      }
    }
  }

  _validateNoOpenNotes = () => {
    if (isNewNoteOpen(this.props.documentNotes) || this.state.activeNoteID) {
      Toastr.showToastr({
        type: "warning",
        message: NOTES_STRINGS.OPEN_NOTES_ERROR,
        timeout: 6000,
      });
      return false;
    }
    return true;
  };

  _openNoteEditor = noteID => {
    if (this._validateNoOpenNotes()) {
      this.setState({
        activeNoteID: noteID,
      });
    }
  };

  _isNotesAllowed = editorState => {
    if (!COMPONENT_FEATURE.DOCUMENT.addNote) {
      return false;
    }
    if (checkEmptySelection(editorState)) {
      return false;
    }
    if (
      checkSelectedTextHasEntity(editorState, DRAFT_ENTITY.NOTES) ||
      checkSelectedTextHasEntity(editorState, DRAFT_ENTITY.DYNAMICFIELD)
    ) {
      Toastr.showToastr({
        type: "warning",
        message: NOTES_STRINGS.ALREADY_AN_ENTITY,
        timeout: 6000,
      });
      return false;
    }
    // restricting add note button for perma/snap/webform
    if (
      window.location &&
      window.location.pathname &&
      (window.location.pathname.includes("/perma/DOC/") ||
        window.location.pathname.includes("/m/") ||
        window.location.pathname.includes("/s/") ||
        window.location.pathname.includes("/f/"))
    ) {
      return false;
    }
    return true;
  };

  _createNewNote = () => {
    const { component, editorState, removeHighlightSelection } = this.props;
    let text = "";
    let isAllowed = true;
    let isValidated = false;
    if (editorState) {
      text = getSelectedText(editorState);
      isAllowed = this._isNotesAllowed(editorState, this.props.mode);
      isValidated = this._validateNoOpenNotes();
    }
    if (text !== "" && isAllowed && isValidated) {
      const { docID, updateSection } = this.props;
      const note = emptyNote(docID, null);
      note.content = {
        highlight: text,
        message: "",
      };
      const selectionState = editorState.getSelection();
      const blockKeyVal = selectionState.getAnchorKey();
      const anchorKeyVal = selectionState.getAnchorKey();
      const focusKeyVal = selectionState.getFocusKey();
      let acnhorOffsetVal = selectionState.getAnchorOffset();
      let focusOffsetVal = selectionState.getFocusOffset();
      const isBackwardSelection = selectionState.isBackward;
      let selectionDataLength = null;
      if (isBackwardSelection) {
        let tempVar = null;
        tempVar = focusOffsetVal;
        focusOffsetVal = acnhorOffsetVal;
        acnhorOffsetVal = tempVar;
      }
      selectionDataLength = focusOffsetVal - acnhorOffsetVal;
      const noteMappingData = {
        componentId: component.id,
        componentType: component.type,
        blockType: component.componentMeta.blockType,
        selectionData: {
          blockKey: blockKeyVal,
          acnhorOffset: acnhorOffsetVal,
          focusOffset: focusOffsetVal,
          length: selectionDataLength,
          anchorKey: anchorKeyVal,
          focusKey: focusKeyVal,
          // entityData,
        },
        clauseOptionId: this.props.clauseOptionsDetails
          ? this.props.clauseOptionsDetails.id
          : null,
        clauseOptionValueId: this.props.activeComponentMeta.clauseOptionValueId,
        clauseOptionType: this.props.clauseOptionsDetails
          ? this.props.clauseOptionsDetails.meta.subtype
          : null,
        // version: "2",
      };
      if (component.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
        const { tableCellId, tableColId, tableRowId } =
          this.props.activeComponentMeta;
        noteMappingData.tableCellId = tableCellId;
        noteMappingData.tableColId = tableColId;
        noteMappingData.tableRowId = tableRowId;
      }
      note.anchor = {
        component: "DOCUMENT",
        action: "HIGHLIGHT",
        noteMappingConfigs: noteMappingData,
      };
      if (this.props.clauseOptionsDetails) {
        const { sharingHash, newMasterDocUrl } = this.props.match.params;
        note.clauseOptionsDetails = this.props.clauseOptionsDetails;
        note.sharingHash = sharingHash;
        note.newMasterDocUrl = newMasterDocUrl;
      }
      note.component = component;
      note.editorState = editorState;
      note.updateSection = updateSection;
      note.removeHighlightSelection = removeHighlightSelection;
      note.activeComponentMeta = this.props.activeComponentMeta;
      this.props.addNewNote(note).then(() => {
        setTimeout(() => {
          this._scrollToNewNote();
        }, 100);
      });
      this.props.changeToolsView("notes");
    } else if (isAllowed && this._validateNoOpenNotes()) {
      const { docID, addNewNote } = this.props;
      const note = emptyNote(docID, null);
      addNewNote(note).then(() => {
        setTimeout(() => {
          this._scrollToNewNote();
        }, 100);
      });
      this._scrollToNewNote();
    }
  };

  _scrollToNewNote = () => {
    const newNote = document.getElementById("new-note-with-null-id");
    if (newNote !== null) {
      newNote.scrollIntoView({
        behavior: "smooth",
      });
      // this._removeActiveComponents();
      newNote.classList.add("new-note-border");
    }
  };

  _addReply = baseID => {
    if (this._validateNoOpenNotes()) {
      const { updateNotesList, documentNotes, docID } = this.props;
      const note = emptyNote(docID, baseID);
      const updatedNotes = documentNotes.map(noteThread => {
        if (noteThread.id === baseID) {
          const replies = noteThread.replies || [];
          noteThread.replies = [...replies, note];
        }
        return noteThread;
      });
      updateNotesList(updatedNotes);
    }
  };

  _discardNote = note => {
    const { documentNotes, updateNotesList } = this.props;
    const { activeNoteID } = this.state;
    let updatedNotes;
    if (note.id === null) {
      if (note.base_comment_id === null) {
        updatedNotes = documentNotes.filter(noteThread => noteThread !== note);
      } else {
        const baseID = note.base_comment_id;
        updatedNotes = documentNotes.map(noteThread => {
          if (noteThread.id === baseID) {
            return {
              ...noteThread,
              replies: noteThread.replies.filter(reply => reply.id !== null),
            };
          }
          return noteThread;
        });
      }
      updateNotesList(updatedNotes);
    } else if (activeNoteID === note.id) {
      this.setState({
        activeNoteID: null,
      });
    }
    // if (note.anchor && note.anchor.action === "HIGHLIGHT" && note.id === null) {
    //   const { component, removeHighlightSelection } = note;
    //   removeHighlightSelection(component.id);
    // }
  };

  _updateNoteList = (note, newNote) => {
    const { documentNotes, updateNotesList } = this.props;
    let updatedNotes;
    if (note.base_comment_id === null) {
      updatedNotes = documentNotes.map(noteThread => {
        if (noteThread.id === note.id && noteThread.anchor === note.anchor) {
          return newNote;
        }
        return noteThread;
      });
    } else {
      updatedNotes = documentNotes.map(noteThread => {
        if (noteThread.id === note.base_comment_id) {
          const updatedThread = {
            ...noteThread,
            replies: noteThread.replies.map(noteItem => {
              if (noteItem.id === note.id && noteItem.anchor === note.anchor) {
                return newNote;
              }
              return noteItem;
            }),
          };
          return updatedThread;
        }
        return noteThread;
      });
    }
    updateNotesList(updatedNotes);
  };

  _createNoteForClauseOption = (
    newEditorState,
    activeComponentMeta,
    clauseOptionsDetails,
  ) => {
    let value = null;
    if (activeComponentMeta.componentType === "HTML") {
      if (clauseOptionsDetails) {
        const {
          meta: { subtype },
          value: values,
        } = clauseOptionsDetails;

        if (subtype === "free_text") {
          this.props.forceUpdateLocalEditorState({
            componentId: activeComponentMeta.componentId,
            editorState: newEditorState,
          });
        } else {
          value = JSON.stringify(
            convertToRaw(newEditorState.getCurrentContent()),
          );
          if (
            (subtype === "optional" || subtype === "multi_select") &&
            Array.isArray(values)
          ) {
            values.forEach(val => {
              if (val.id === activeComponentMeta.clauseOptionValueId) {
                val.value = value;
              }
            });
          } else if (subtype === "single_select") {
            values.value = value;
          }

          this.props.forceRerenderWhenClauseOptionsUpdate({
            sectionId: activeComponentMeta.componentId,
            clauseOptionsDetails,
          });
          this.props.forceUpdateLocalEditorState({
            componentId: activeComponentMeta.componentId,
            editorState: null,
          });
        }
      }
    } else if (activeComponentMeta.componentType === "Table") {
      const updatedSection = getUpdatedParentComponent(
        this.props.components,
        activeComponentMeta,
        {
          id: activeComponentMeta.tableCellId,
          data: newEditorState.getCurrentContent(),
          editorState: newEditorState,
        },
      );
      value = updatedSection.data;
      this.props.updateComponentEditorState({
        componentMeta: activeComponentMeta,
        editorState: newEditorState,
        mode: "DOCUMENT",
      });
      this.props.forceUpdateLocalEditorState({
        componentId: activeComponentMeta.tableCellId,
        editorState: newEditorState,
      });
    }
  };

  _updateSectionPostNoteAddion = (note, noteId) => {
    const { editorState, activeComponentMeta } = note;
    const { updatedEditorState } = createHighlightNotes(editorState, {
      noteId,
      id: note.anchor.id,
    });

    if (note.component && note.component.clauseOptions && this.props.showForm) {
      const { clauseOptionsDetails } = note;
      this._createNoteForClauseOption(
        updatedEditorState,
        activeComponentMeta,
        clauseOptionsDetails,
      );
    } else if (this.props.showForm) {
      let decideActiveCMPId = activeComponentMeta.componentId;
      if (activeComponentMeta.componentType === "Table") {
        decideActiveCMPId = activeComponentMeta.tableCellId;
      }
      this.props.forceUpdateLocalEditorState({
        componentId: decideActiveCMPId,
        editorState: updatedEditorState,
      });
    } else if (this.props.isSharedDocument || this.props.isLocked) {
      const clickedComponentID =
        activeComponentMeta.componentType === "Table"
          ? activeComponentMeta.tableCellId
          : activeComponentMeta.componentId;
      this.props.forceUpdateLocalEditorState({
        componentId: clickedComponentID,
        editorState: updatedEditorState,
      });
    } else {
      this.props.initActiveEditor(
        updatedEditorState,
        note.component,
        activeComponentMeta,
      );
      this.props.updateComponentMeta(activeComponentMeta);
      const clickedComponentID = activeComponentMeta.componentId;
      // activeComponentMeta.componentType === "Table"
      //   ? activeComponentMeta.tableCellId
      //   : activeComponentMeta.componentId;
      this.props.updateDocumentClickedComponentID(
        clickedComponentID,
        true,
        activeComponentMeta.componentType,
      );
    }
    delete note.component;
    delete note.updateSection;
    delete note.editorState;
    delete note.clauseOptionsDetails;
    delete note.sharingHash;
    delete note.newMasterDocUrl;
  };

  _saveNote = saveNote => {
    let note = {
      ...saveNote,
    };

    if (
      "draftJSNote" in note.content &&
      Array.isArray(note.content.draftJSNote)
    ) {
      const message = note.content.draftJSNote
        .map(df => df.blocks.map(block => block.text).join("\n"))
        .join("\n");
      note.content = {
        ...note.content,
        message,
      };
    }

    if (note.id === null) {
      const { createNote, isSharedDocument } = this.props;
      const noteEntitykey = uuid.v4();

      note.anchor.id = noteEntitykey;
      // annotaions case(means adding notes in a editor)
      if (
        note.anchor.noteMappingConfigs &&
        note.anchor.noteMappingConfigs.selectionData
      ) {
        note.anchor.noteMappingConfigs.selectionData.key = noteEntitykey;
      }

      let postNote = createNote;
      if (isSharedDocument) {
        const { docID, sharingHash } = this.props;
        postNote = this.props.createSharedDocumentNote;
        note = {
          note,
          docID,
          sharingHash,
        };
      }
      postNote(note).then(response => {
        const { status, docID, docType } = this.props;
        const newNote = {
          ...response.Comment,
          replies: note.replies,
        };

        // check if it is highlighted notes. Then first update section data
        const noteData = isSharedDocument ? note.note : note;
        if (noteData.anchor && noteData.anchor.action === "HIGHLIGHT") {
          this._updateSectionPostNoteAddion(noteData, response.Comment.id);
        }

        if (isSharedDocument) {
          this._updateNoteList(note.note, newNote);
        } else {
          this._updateNoteList(note, newNote);
        }
        // Implementing amplitude for comment adding
        AmplitudeUtils.amplitudeLogEvent(EVENTS.EVENT_NAMES.COMMENT_ADDED, {
          Tagged: Object.keys(newNote.anchor).length > 0,
          Mention: newNote.mentions && newNote.mentions.length > 0,
          Reply: newNote.base_comment_id !== null,
          Status: status,
          Document_id: docID,
          Document_type: docType,
        });
      });
    } else {
      const { editNoteByID } = this.props;
      editNoteByID(note).then(response => {
        const newNote = {
          ...response.Comment,
          replies: note.replies,
        };
        this._updateNoteList(note, newNote);
        if (this.state.activeNoteID === note.id) {
          this.setState({
            activeNoteID: null,
          });
        }
      });
    }
  };

  _deleteNote = note => {
    const { deleteNoteByID } = this.props;
    deleteNoteByID(note.id).then(() => {
      const { documentNotes, updateNotesList } = this.props;
      let updatedNotes;
      if (note.base_comment_id === null) {
        updatedNotes = documentNotes.filter(
          noteThread => noteThread.id !== note.id,
        );
      } else {
        updatedNotes = documentNotes.map(noteThread => {
          if (noteThread.id === note.base_comment_id) {
            const updatedNote = {
              ...noteThread,
              replies: noteThread.replies.filter(
                noteItem => noteItem.id !== note.id,
              ),
            };
            return updatedNote;
          }
          return noteThread;
        });
      }
      updateNotesList(updatedNotes);
    });
  };

  _acceptRejectChanges = (note, accept) => {
    const { editNoteByID } = this.props;
    const { anchor } = note;
    // check accepted or rejected state
    const updatedNote = {
      ...note,
      comment_status: accept ? "Accepted" : "Rejected",
    };
    editNoteByID(updatedNote)
      .then(response => {
        const newNote = {
          ...response.Comment,
        };
        this._updateNoteList(updatedNote, newNote);

        if (
          (anchor.action === "DELETED" && accept) ||
          (anchor.action === "INSERTED" && !accept)
        ) {
          this.props.acceptRejectSectionChanges(
            anchor.component_id,
            anchor.id,
            false,
            "DOCUMENT",
          );
        } else {
          this.props.acceptRejectSectionChanges(
            anchor.component_id,
            anchor.id,
            true,
            "DOCUMENT",
          );
        }
      })
      .catch(error => {
        this._updateNoteList(note, note);
        Toastr.showToastr({
          type: "failure",
          message:
            error && error.response && error.response.status === 403
              ? "You are not allowed accept/reject Notes for this document"
              : "Unable to accept/reject. Please try after some time.",
          timeout: 6000,
        });
      });
    note.comment_status = "OPEN";
  };

  _emptyNotesView = () => (
    <div className="notes-empty-state">
      <span className="addproduct-info">
        {" "}
        {NOTES_STRINGS.EMPTY_NOTES_STRING}
      </span>{" "}
      <div className="notes-empty-icon">
        <i className="notes-empty-icon-front">
          <FontAwesomeIcon icon={["fas", "note"]} />{" "}
        </i>{" "}
        <i className="notes-empty-icon-back">
          <FontAwesomeIcon icon={["fas", "note"]} />{" "}
        </i>{" "}
      </div>{" "}
    </div>
  );

  _notesThreadView = () => {
    const { documentNotes, uploadNoteAttachment, isSharedDocument } =
      this.props;
    const { activeNoteID } = this.state;
    const author = readDataFromLocalStorage("revvsalesUser");
    if (!Array.isArray(documentNotes) || documentNotes.length === 0) {
      return this._emptyNotesView();
    }
    return documentNotes.map(notesThread => (
      <NotesThread
        key={notesThread.id}
        author={author}
        noteThread={notesThread}
        openNoteEditor={this._openNoteEditor}
        activeNoteID={activeNoteID}
        discardNote={this._discardNote}
        addReply={this._addReply}
        saveNote={this._saveNote}
        deleteNote={this._deleteNote}
        uploadAttachment={uploadNoteAttachment}
        acceptRejectChanges={this._acceptRejectChanges}
        isSharedDocument={isSharedDocument}
      />
    ));
  };

  render() {
    return (
      <div className="context-notes  animated fadeinright">
        <NotesHeader
          addNote={this._createNewNote}
          updateToolPaneData={this.props.updateToolPaneData}
          isSharedDocument={this.props.isSharedDocument}
        />{" "}
        <div className="notes-container">
          {" "}
          <div className="content-scroll">{this._notesThreadView()} </div>
        </div>{" "}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { documentNotes, mentionUsers } = state.NotesStore;
  const {
    document: { id: docID, status, docType, showForm, isLocked },
    components = {},
  } = state.DocumentStore;
  const {
    activeEditorState: editorState,
    activeComponent,
    activeComponentInfo: activeComponentMeta,
  } = state.EditorStore;

  return {
    documentNotes,
    docID,
    mentionUsers,
    isLocked,
    status,
    docType,
    components,
    showForm,
    editorState,
    component: activeComponent,
    activeComponentMeta,
  };
};

const mapDispatchToProps = {
  getAllDocNotes,
  acceptRejectSectionChanges,
  getDocNotes: (docID, docType) => getNotesByDocID(docID, docType),
  addNewNote: note => addNote(note),
  updateNotesList: noteList => updateNotes(noteList),
  createNote: note => postNewNote(note),
  createSharedDocumentNote: note => createSharedDocumentNote(note),
  editNoteByID: (noteID, data) => editNote(noteID, data),
  deleteNoteByID: noteID => deleteNote(noteID),
  uploadNoteAttachment: formData => uploadAttachment(formData),
  fetchAllUsersForMentions,
  updateDocumentClickedComponentID,
  initActiveEditor,
  updateToolPaneData,
  updateComponentEditorState,
  updateComponentMeta,
  updateDynamicFieldsInDoc,
  updateDynamicFieldsInMasterDoc,
  forceRerenderWhenClauseOptionsUpdate,
  forceUpdateLocalEditorState,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notes);
